<h2 mat-dialog-title>Welcome to the EUROVOLC tool for observations of volcanic events</h2>
<mat-dialog-content class="mat-typography">
  <p>This tool is a platform for collating observations from people witnessing volcanic phenomena at European and other
    volcanoes. The tool allows you to contribute your own observations, as well as to access observations collected
    through other citizen science tools available across Europe, including <a href="https://www.bgs.ac.uk/myVolcano/"
      target="_blank">myVolcano</a> (British Geological Survey); <a
      href="https://www.vedur.is/skraning_brennisteinsmengun/" target="_blank">sulphur dioxide reporting</a>
    (Iceland Met Office); and <a href="http://www.ingv.it/it/" target="_blank">Osservatorio Vesuviano web questionnaire
      & Tefranet
      (INGV-Catania)</a>. The EUROVOLC tool
    harvests the data from the other citizen science tools on a monthly basis. </p>


  <p>Users are able to view and download the data and filter by, for example, date, geographical location, type of
    observation, or a specific citizen science tool.</p>

  <p>The tool was developed as part of The European Network of Observatories and Research Infrastructures for
    Volcanology (<a href="https://eurovolc.eu/" target="_blank">EUROVOLC</a>) project with the objective of raising
    awareness and sharing data on volcanic hazards.</p>

  <h2>Referencing the EUROVOLC tool</h2>

  <p>When uploading data to the EUROVOLC citizen science tool, please refer to the <a
      href="https://www.bgs.ac.uk/legal-and-policy/terms-of-use/" target="_blank">Terms of Use</a> and <a
      href="https://www.bgs.ac.uk/legal-and-policy/privacy/" target="_blank">Privacy Policy</a>. When
    using the data please reference the original tool (Source) or use the following reference when using data recorded
    by the EUROVOLC tool:</p>

  <blockquote>
    EUROVOLC citizen science tool, July 2021, version 1. Retrieved from https://eurovolc.bgs.ac.uk [DATE]
  </blockquote>

  <p>Disclaimer: the veracity of the data cannot be guaranteed, data validation extends only to the removal of malicious
    content. </p>

  <h2>Authors and contributors:</h2>

  <ul>
    <li>Evgenia Ilyinskaya - University of Leeds, UK</li>
    <li>Laura Sandri - Istituto Nazionale di Geofisica e Vulcanologia, Sezione di Bologna, Italy</li>
    <li>Melanie Duncan - British Geological Survey (UK Research and Innovation), UK</li>
    <li>Martin Nayembil - British Geological Survey (UK Research and Innovation), UK</li>
    <li>Danilo Reitano - Istituto Nazionale di Geofisica e Vulcanologia, Sezione di Catania, Italy</li>
    <li>Sara Barsotti - Icelandic Meteorological Office, Iceland</li>
    <li>Sue Loughlin - British Geological Survey (UK Research and Innovation), UK</li>
    <li>Rosella Nave - Istituto Nazionale di Geofisica e Vulcanologia, Osservatorio Vesuviano, Italy</li>
    <li>Katy Mee - British Geological Survey (UK Research and Innovation), UK</li>
    <li>Adelina Geyer - ICTJA-CSIC, Institute of Earth Sciences Jaume Almera, Spain</li>
    <li>Jacopo Selva - Istituto Nazionale di Geofisica e Vulcanologia, Bologna, Italy</li>
    <li>Domenico Mangione - Dipartimento della Protezione Civile, Rome, Italy</li>
    <li>Chiara Cristiani - Dipartimento della Protezione Civile, Rome, Italy</li>
    <li>Antonio Ricciardi - Dipartimento della Protezione Civile, Rome, Italy</li>
    <li>Giovanni Scarpato - Istituto Nazionale di Geofisica e Vulcanologia, Osservatorio Vesuviano, Italy</li>
    <li>Rosa Nappi Istituto - Nazionale di Geofisica e Vulcanologia, Osservatorio Vesuviano, Italy</li>
  </ul>

  <h2>Platform development team:</h2>

  <p>Steve Richardson, Jon Stuteley, Andy Bevan, Johann Dascalescu, Roman Roth and Liam Spencer (British Geological
    Survey, UK Research and Innovation), UK. </p>

  <p>The tool is hosted by the British Geological Survey, UK.</p>

  <h2>Partner Tools</h2>

  <p>The tool has been developed as part of the European Network of Observatories and Research Infrastructures for
    Volcanology (EUROVOLC) project. Further details about EUROVOLC and the citizen science tools can be found via the
    links below.</p>

  <h3>EUROVOLC Project</h3>
  <p>Website: <a href="https://eurovolc.eu/" target="_blank">https://eurovolc.eu/</a></p>

  <h3>Icelandic Met Office – SO2_IMO</h3>
  <p>The IMO SO2 tool is an online form for people to register if they have smelt SO2 during an eruption and how it
    affected them. Website: <a href="https://www.vedur.is/skraning_brennisteinsmengun/"
      target="_blank">https://www.vedur.is/skraning_brennisteinsmengun/</a></p>

  <h3>INGV – TefraNet and Seismic Events</h3>
  <p>TefraNet is a system designed to quickly alert INGV to locations and details of tephra fallout during eruptions,
    information that is collected by a network of smartphone users. INGV also gathers reports of felt earthquakes from
    citizens.</p>
  <p></p>Website INGV: <a href="http://www.ingv.it/it/" target="_blank">http://www.ingv.it/it/</a>
  <p></p>Website INGV-OE (Etna Observatory): <a href="http://www.ct.ingv.it/" target="_blank">http://www.ct.ingv.it/</a>
  <p></p>Volcano Observatory Notice for Aviation (VONA):
  <a href="http://www.ct.ingv.it/index.php/monitoraggio-e-sorveglianza/prodotti-del-monitoraggio/comunicati-vona"
    target="_blank">http://www.ct.ingv.it/index.php/monitoraggio-e-sorveglianza/prodotti-del-monitoraggio/comunicati-vona</a>

  <h3>British Geological Survey – myVolcano</h3>
  <p>myVolcano is a mobile phone citizen science app developed by the BGS for sharing observations of volcanic hazards
    and phenomena. The app enables users to upload descriptions and photographs of any type of volcanic phenomena they
    may have witnessed from volcanoes around the world.
    Website: <a href="https://www.bgs.ac.uk/myVolcano/" target="_blank">https://www.bgs.ac.uk/myVolcano/</a></p>

  <h3>Volcano information</h3>
  <p>Volcano information (including locations) comes from the Smithsonian Institution’s Global Volcanism Program and,
    where applicable, the European Catalogue of Volcanoes.</p>
  <p></p>European Catalogue of Volcanoes: <a href="http://volcanoes.eurovolc.eu/"
    target="_blank">http://volcanoes.eurovolc.eu/</a>
  <p></p>Smithsonian Institution Global Volcanism Program: <a href="https://volcano.si.edu/"
    target="_blank">https://volcano.si.edu/</a>
  <p></p>Global Volcanism Program, 2013. Volcanoes of the World, v. 4.9.0 (04 Jun 2020). Venzke, E (ed.). Smithsonian
  Institution. Downloaded 10 Jun 2020. <a href="https://doi.org/10.5479/si.GVP.VOTW4-2013"
    target="_blank">https://doi.org/10.5479/si.GVP.VOTW4-2013</a>.
  <br />
  <br />
  <span>version: {{version}}</span>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button [mat-dialog-close]="true" cdkFocusInitial>OK</button>
</mat-dialog-actions>